import { useState } from "react"
import { refreshOnlyFor } from "lib/graphql/api"
import { invalidate } from "lib/graphql/cache"
import { scheduleAssignmentRegime } from "routes/skill-mapper/controller/schedule-assignment-regime"
import { skillMapScheduleAssignments } from "routes/skill-mapper/controller/skill-map-schedule-assignments"

/**
 * What the assignment board reads (FIQ-2457): the regime's schedules and the skill map's
 * assignments, and whether loading either failed.
 *
 * Walking the regime is expensive and assignment edits never change its result, so it is only
 * fetched again when this regime is written (the server invalidates it with the tree's id) or
 * the library is (invalidated with no id, so every board refreshes); see
 * signalRegimeSourceChanged on the server.
 *
 * useResults keeps the last result through a failed refresh, so the board stays on screen and
 * refreshFailed says it may be out of date. regimeFailed / assignmentsFailed mean nothing has
 * loaded at all. The board is keyed by skill map and regime (see ScheduleAssignmentsPanel), so
 * one regime's list is never shown while another loads.
 *
 * @param {{ regimeId: string, skillMapId: string }} options
 */
export function useRegimeAssignmentData({ regimeId, skillMapId }) {
    const [regimeError, setRegimeError] = useState(null)
    const [assignmentsError, setAssignmentsError] = useState(null)
    const summary = scheduleAssignmentRegime.useResults.notify(noteRegime, { regimeId }, refreshOnlyFor(regimeId))
    const assignments = skillMapScheduleAssignments.useResults.notify(noteAssignments, { skillMapId })

    return {
        summary,
        assignments,
        regimeFailed: !!regimeError && summary == null,
        assignmentsFailed: !!assignmentsError && assignments == null,
        refreshFailed: (!!regimeError && summary != null) || (!!assignmentsError && assignments != null),
        retry,
    }

    function noteRegime(result) {
        setRegimeError(result instanceof Error ? result : null)
    }

    function noteAssignments(result) {
        setAssignmentsError(result instanceof Error ? result : null)
    }

    function retry() {
        if (regimeError) invalidate("scheduleAssignmentRegime")
        if (assignmentsError) invalidate("skillMapScheduleAssignments")
    }
}
